/* designed to be used as a singleton, and injected into all components
so we have a single point of entry to audio, which avoids multiple audio
playing over top of each other, and allows us to manage the icon states
across different components.  also, by not having actual audio elements
in the html this avoids pre-loading the TTS urls, and saves money. 
The AudioManager maintains a LIFO queue of audio objects keyed by the URL.
We can preload URLs so they can be played instantly. If we call play(url)
and we already have an Audio object associated with that URL, we should call
play on it. If we call play(url) and it hasn't been loaded, we should first
call preload(url). */
export class AudioManager {
  constructor() {
    this.audioStack = []
    this.playback_speed = 1
    this.playing = false
    this.onended = () => {}  // default to a no-op
  }

  // should only be called from the settings modal
  setPlaybackSpeed(speed) {
    this.playback_speed = speed
  }

  preload(url) {
    // Remove any existing broken audio object for this URL
    this.removeFromStack(url)
    
    console.log("preloading url", url)
    const audio = new Audio(url)
    audio.preload = 'auto'
    
    // Mark as broken if error occurs during load
    audio.onerror = () => {
      console.error(`Audio failed to load (likely 500 error): ${url}`)
      this.removeFromStack(url)
    }
    
    this.audioStack.push({ url, audio })
  }

  removeFromStack(url) {
    const index = this.audioStack.findIndex(obj => obj.url === url)
    if (index !== -1) {
      this.audioStack.splice(index, 1)
    }
  }

  play(url) {
    console.log("play url", url)
    this.playing = false

    let audioObj = this.audioStack.find(obj => obj.url === url)
    
    // Check if existing audio object is in error state
    if (audioObj && audioObj.audio.error) {
      console.log("Found broken audio object, removing and creating new one")
      this.removeFromStack(url)
      audioObj = null
    }
    
    if (!audioObj) {
      this.preload(url)
      audioObj = this.audioStack.find(obj => obj.url === url)
    } else {
      console.log("found audio object in stack")
      // Move the existing audio object to the head of the queue
      const index = this.audioStack.findIndex(obj => obj.url === url)
      this.audioStack.splice(index, 1)
      this.audioStack.push(audioObj)
    }
    
    console.log("ultimately we got this audio object", audioObj)
    
    if (!audioObj || !audioObj.audio) {
      console.error("Failed to create audio object for", url)
      return
    }
    
    this.audio = audioObj.audio
    this.audio.playbackRate = this.playback_speed
    
    // Add error handler for play attempts
    this.audio.onerror = () => {
      console.error(`Audio playback error for ${url}`)
      this.removeFromStack(url)
      this.onended_wrapper()
    }
    
    return this.play_with_retry(url)
  }

  /* if the audio fails to play, try again with exponential backoff and a hard limit of 5 attempts */
  async play_with_retry(url) {
    console.log("entering play_with_retry", url)
    let attempts = 0
    while (attempts < 5) {
      try {
        this.audio.playbackRate = this.playback_speed
        this.audio.onended = this.onended_wrapper.bind(this)
        await this.audio.play()
        this.playing = true
        console.log('play succeeded after', attempts, 'attempts')
        return
      } catch (err) {
        attempts++
        console.error('trapped play error', err, 'retrying', attempts)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempts)))
      }
    }
    console.error('Audio failed to play after maximum retries')
    this.removeFromStack(url)
    this.onended_wrapper()
  }

  stop() {
    if (this.audio) {
      this.audio.pause()
      this.audio.currentTime = 0
      this.playing = false
    } else {
      console.error("no audio object to stop")
    }
  }

  onended_wrapper() {
    this.playing = false
    this.onended()
  }

  pause() {
    if (this.audio) {
      this.audio.pause()
    } else {
      console.error("no audio object to pause")
    }
    this.onended_wrapper()
  }

  setOnEnded(callback) {
    this.onended = callback
    if (this.audio) {
      this.audio.onended = this.onended_wrapper.bind(this)
    } else {
      console.error("no audio object to set onended")
    }
  }
}

